import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { ArrowRight, Clock3 } from "lucide-react";

import { AuthLayout } from "@/app/layouts/AuthLayout";
import { AuthHeading } from "@/features/auth/presentation/components/AuthHeading";
import { Button } from "@/shared/components/ui/button";
import { useAppDispatch, useAppSelector } from "@/store";
import { selectIsAuthenticated, selectSessionLost } from "@/store/selectors";
import { consumeSessionLost } from "@/store/slices/authSlice";

/**
 * Pantalla dedicada cuando el refresh falló a media navegación.
 *
 * Flujo del flag `sessionLost`:
 *   - `baseQuery` lo prende (`sessionExpired` action) al fallar refresh.
 *   - `AuthGuard` ve el flag → redirige acá.
 *   - Esta pantalla captura el valor en el mount via `useState(() => ...)`
 *     y lo consume en un `useEffect` — así se muestra una vez y una
 *     recarga posterior cae a /login (no queda loop en la pantalla).
 */
export function SessionExpiredPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const sessionLost = useAppSelector(selectSessionLost);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  // Snapshot del flag al mount — garantiza que si entramos con el flag
  // encendido la pantalla se renderiza aunque el effect lo consuma.
  const [wasExpired] = useState(() => sessionLost);

  useEffect(() => {
    if (sessionLost) dispatch(consumeSessionLost());
    // Intencional: una sola vez al mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isAuthenticated) return <Navigate to="/" replace />;
  // Acceso directo sin razón (URL copiada, back/forward) → a login.
  if (!wasExpired) return <Navigate to="/login" replace />;

  return (
    <AuthLayout
      visualTitle="La seguridad primero, siempre."
      visualSubtitle="Cerramos las sesiones inactivas por tu cuenta y la de las usuarias que atendemos."
    >
      <AuthHeading
        kicker="Sesión expirada"
        icon={<Clock3 className="h-5 w-5" />}
        title="Tu sesión caducó"
        subtitle="Por seguridad cerramos tu sesión después de un periodo de inactividad. Vuelve a iniciar sesión para continuar donde estabas."
      />

      <div className="space-y-5">
        <Button
          type="button"
          onClick={() => navigate("/login", { replace: true })}
          className="h-12 w-full text-[14px] font-medium shadow-sm transition-all hover:shadow-md"
        >
          Volver a iniciar sesión
          <ArrowRight className="h-4 w-4" />
        </Button>

        <div className="rounded-xl bg-muted/50 p-4">
          <p className="text-[12px] leading-relaxed text-muted-foreground">
            <span className="font-medium text-foreground">¿Por qué pasó esto?</span> Tu token de
            acceso venció y no pudimos renovarlo automáticamente. Puede deberse a inactividad
            prolongada, cambio de red, o que la sesión fue invalidada desde otro dispositivo.
          </p>
        </div>
      </div>

      <div className="mt-10 border-t border-border/60 pt-6">
        <p className="text-center text-[12px] text-muted-foreground">
          Si crees que esto fue un error,{" "}
          <Link
            to="/login"
            className="font-medium text-foreground/80 underline-offset-4 transition-colors hover:text-foreground hover:underline"
          >
            inicia sesión
          </Link>{" "}
          y avísanos a{" "}
          <a
            href="mailto:soporte@arya.pe"
            className="font-medium text-foreground/80 underline-offset-4 transition-colors hover:text-foreground hover:underline"
          >
            soporte@arya.pe
          </a>
          .
        </p>
      </div>
    </AuthLayout>
  );
}
